<template>
    <div>
        <pendo-actions-option
            v-if="showActionsOption"
            v-pendo-tooltip="{
                content: tooltipContent,
                multiline: true,
                arrow: true,
            }"
            class="voc-item-multiselect__option"
            show-actions-on-disabled-groups
            :option="formattedOption"
            :index="index"
            :actions="actions"
            @edit="editOption"
            @delete="deleteOption"
            @mouseover="onMouseover" />
        <div v-else>
            <div
                v-if="isCategory"
                v-pendo-tooltip="{
                    content: tooltipContent,
                    multiline: true,
                    arrow: true,
                }"
                class="voc-item-multiselect__category"
                @mouseover="onMouseover">
                {{ option.category }}
            </div>
            <div
                v-if="option.isTag"
                v-pendo-tooltip="{
                    content: tooltipContent,
                    multiline: true,
                    arrow: true,
                }"
                class="voc-item-multiselect__new-option"
                data-cy="voc-item-multiselect-new-option"
                @mouseover="onMouseover">
                {{ option[labelKey] }} (Add New)
            </div>
            <div
                v-pendo-tooltip="{
                    content: tooltipContent,
                    multiline: true,
                    arrow: true,
                }"
                class="voc-item-multiselect__option"
                :class="{
                    'voc-item-multiselect__option--no-padding': type !== 'label',
                }"
                data-cy="voc-item-multiselect-option"
                @mouseover="onMouseover">
                <product-area-icon
                    v-if="color"
                    :color="color" />
                <pendo-app-icon
                    v-if="type === 'app'"
                    :app="option" />
                <div class="voc-item-multiselect__option-label">
                    {{ label }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { PendoTooltip, PendoAppIcon, PendoActionsOption } from '@pendo/components';
import { elementHasOverflow, findNodeByContent } from '@/utils/dom.utils';
import ProductAreaIcon from '@/voc/components/icons/product-area/product-area.icon.vue';
import { mapGetters, mapActions, mapState } from 'vuex';
import { MODALS } from '@/voc/constants/overlays.js';
import { formatLabels } from '@/voc/helpers/common.helper';

export default {
    name: 'VocItemMultiselectOption',
    components: {
        PendoAppIcon,
        PendoActionsOption,
        ProductAreaIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        option: {
            type: Object,
            required: true,
        },
        labelKey: {
            type: String,
            default: 'label',
        },
        type: {
            type: String,
            required: true,
        },
        color: {
            type: String,
            default: null,
        },
        location: {
            type: String,
            default: null,
        },
        index: {
            type: Number,
            default: null,
        },
    },
    data () {
        return {
            tooltipContent: null,
        };
    },
    computed: {
        ...mapState({
            labels: (state) => state.voc.labels.labelOptions,
        }),
        ...mapGetters({
            canEditVocItems: 'vocItems/canEditVocItems',
        }),
        isCategory () {
            return this.type === 'label' && this.option.type === 'group' && !this.option.isTag;
        },
        label () {
            return this.option.associatedItemCount !== undefined
                ? `${this.option[this.labelKey]} (${this.option.associatedItemCount})`
                : this.option[this.labelKey];
        },
        actions () {
            return [
                ...(this.showLabelActions
                    ? [
                        {
                            type: 'edit',
                            tooltip: 'Edit label',
                            icon: {
                                type: 'edit',
                            },
                        },
                        {
                            type: 'delete',
                            tooltip: 'Delete label',
                            icon: {
                                type: 'trash-2',
                            },
                        },
                    ]
                    : []),
                ...(this.showCategoryActions
                    ? [
                        {
                            type: 'edit',
                            tooltip: 'Edit category',
                            icon: {
                                type: 'edit',
                            },
                        },
                        {
                            type: 'delete',
                            tooltip: 'Delete category',
                            icon: {
                                type: 'trash-2',
                            },
                        },
                    ]
                    : []),
            ];
        },
        showActionsOption () {
            return this.actions.length > 0;
        },
        showLabelActions () {
            return (
                this.type === 'label' &&
                this.option.type !== 'group' &&
                !this.option.isTag &&
                this.canEditVocItems &&
                this.location !== 'rule-builder'
            );
        },
        showCategoryActions () {
            return (
                this.isCategory &&
                this.canEditVocItems &&
                this.location !== 'rule-builder' &&
                this.option.id !== 'no-category'
            );
        },
        formattedOption () {
            return {
                ...this.option,
                [this.labelKey]:
                    this.option.associatedItemCount !== undefined
                        ? `${this.option[this.labelKey]} (${this.option.associatedItemCount})`
                        : this.option[this.labelKey],
            };
        },
    },
    methods: {
        ...mapActions({
            openModal: 'voc/modals/openModal',
        }),
        onMouseover ({ currentTarget }) {
            const label = this.option.associatedItemCount
                ? `${this.option[this.labelKey]} (${this.option.associatedItemCount})`
                : this.option[this.labelKey];
            const contentNodes = findNodeByContent(this.option[this.labelKey], currentTarget);

            if (!contentNodes.length) {
                return;
            }

            switch (this.type) {
                case 'label':
                    if (this.option.type !== 'group') {
                        this.tooltipContent = elementHasOverflow(contentNodes[0]) ? label : null;
                    } else {
                        this.tooltipContent = elementHasOverflow(contentNodes[0]) ? this.option.category : null;
                    }
                    break;
                default:
                    this.tooltipContent = elementHasOverflow(contentNodes[0]) ? label : null;
                    break;
            }
        },
        editOption () {
            if (this.option.type === 'group') {
                this.openModal({ modal: MODALS.editCategory, metadata: { category: this.option } });
            } else {
                this.openModal({ modal: MODALS.editLabel, metadata: { label: this.option } });
            }
        },
        deleteOption () {
            if (this.option.type === 'group') {
                const fullCategory = formatLabels(this.labels).find((category) => category.id === this.option.id);
                this.openModal({ modal: MODALS.deleteCategory, metadata: { category: fullCategory } });
            } else {
                this.openModal({ modal: MODALS.deleteLabel, metadata: { label: this.option } });
            }
        },
    },
};
</script>

<style lang="less" scoped>
.voc-item-multiselect {
    &__option {
        padding-left: 10px;
        line-height: 30px;
        display: flex;
        align-items: center;
        gap: 8px;
        text-transform: none;

        &--no-padding {
            padding-left: 0;
        }

        svg {
            flex-shrink: 0;
        }

        &-label {
            flex-grow: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        :deep(.pendo-multiselect-option__label-container) {
            display: flex;
        }

        :deep(.pendo-multiselect-option__label) {
            display: inline-block;
            text-overflow: ellipsis;
            width: 100%;
            white-space: nowrap;
            overflow: hidden;
        }

        :deep(.pendo-multiselect-option__action) {
            height: 100%;
            display: flex;
        }
    }

    &__category {
        line-height: 30px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__new-option {
        line-height: 30px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>
